import { useState, useEffect, useCallback, useRef } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCashback } from '@/services/cashback.service';

type CashbackHistoryTabProps = {
    closeModal: () => void;
};

type AccrualItem = {
    id: string;
    periodMonth: number;
    periodYear: number;
    periodLabel: string;
    status: string;
    tierApplied: string;
    totalSpend: number;
    cashbackEarned: number;
    amountRedeemed: number;
    transactionCount: number;
    vestedAt: string | null;
};

export const CashbackHistoryTab = ({ }: CashbackHistoryTabProps) => {
    const [accruals, setAccruals] = useState<AccrualItem[]>([]);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const { getCashbackAccruals } = useCashback();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile();

    // Fetch accruals
    const fetchAccruals = useCallback(async (pageNum: number) => {
        if (isLoading) return;

        setIsLoading(true);
        const { data, isSuccess } = await getCashbackAccruals(pageNum, 10) ?? {};

        if (isSuccess && data) {
            setAccruals(prev => pageNum === 1 ? data.items : [...prev, ...data.items]);
            setHasMore(pageNum < data.totalPages);
        }

        setIsLoading(false);
    }, [getCashbackAccruals, isLoading]);

    // Initial load
    useEffect(() => {
        fetchAccruals(1);
    }, []);

    // Infinite scroll
    useEffect(() => {
        if (isLoading) return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });

        if (lastElementRef.current) {
            observerRef.current.observe(lastElementRef.current);
        }

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [isLoading, hasMore]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchAccruals(page);
        }
    }, [page]);

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(amount).replace('$', '');
    };

    // Format date
    const formatDate = (periodLabel: string) => {
        return periodLabel;
    };

    return (
        <div className='w-full'>
            {isMobile ? (
                <div className="grid grid-cols-1 gap-[1.2rem] sm:grid-cols-[repeat(auto-fill,minmax(min(100%,28rem),1fr))]">
                    {accruals.map((accrual) => (
                        <article key={accrual.id} className="min-w-0 rounded-[1.2rem] border border-primary p-[1.2rem] text-[1.3rem]">
                            <dl className="space-y-[1rem] [overflow-wrap:anywhere]">
                                <div><dt className="text-second">Tier &amp; Rate</dt><dd>{accrual.tierApplied}</dd></div>
                                <div><dt className="text-second">Date</dt><dd>{formatDate(accrual.periodLabel)}</dd></div>
                                <div><dt className="text-second">Earned</dt><dd>${formatCurrency(accrual.cashbackEarned)}</dd></div>
                                <div><dt className="text-second">Spend</dt><dd>${formatCurrency(accrual.totalSpend)}</dd></div>
                                <div><dt className="text-second">Fee</dt><dd>${formatCurrency(accrual.amountRedeemed)}</dd></div>
                            </dl>
                        </article>
                    ))}
                    {isLoading && <Skeleton className="h-[10rem] w-full" />}
                </div>
            ) : <div className='overflow-x-auto'>
                <table className='w-full'>
                    <thead>
                        <tr className='border-b dark:border-[#717171] border-[#CECECE]'>
                            <th className='text-left py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Tier & Rate
                            </th>
                            <th className='text-left py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Date
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Earned
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Spend
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Fee
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {accruals.map((accrual) => {

                            return (
                                <tr
                                    key={accrual.id}

                                    className='border-b dark:border-[#717171] border-[#CECECE] hover:dark:bg-[#1A1A1A] hover:bg-[#F6F6F6] transition-colors'
                                >
                                    <td className='py-[1.2rem] px-[1.6rem] text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        {accrual.tierApplied}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        {formatDate(accrual.periodLabel)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.cashbackEarned)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.totalSpend)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.amountRedeemed)}
                                    </td>
                                </tr>
                            );
                        })}
                        {isLoading && (
                            <tr>
                                <td colSpan={5} className='py-[2rem] text-center'>
                                    <div className='space-y-[1rem]'>
                                        <Skeleton className='h-[1.8rem] w-[70%] mx-auto' />
                                        <Skeleton className='h-[1.8rem] w-[50%] mx-auto' />
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            }
            <div ref={lastElementRef} className="h-px" aria-hidden="true" />

            {accruals.length === 0 && !isLoading && (
                <div className='py-[4rem] text-center'>
                    <p className='text-[1.4rem] dark:text-[#929292] text-[#717171]'>No history found</p>
                </div>
            )}
        </div>
    );
};
import { Skeleton } from '@/components/ui/skeleton';
